// Count real hands from REAL records (Paul, 9 Oct 2026). The deal facts live in
// public/data/real-count-hands.json (written by scripts/_build-real-count-hands.js from
// BBO vugraph LIN files); this module turns them into puzzles in the shared trainer's
// schema - the same schema countingHandsGenerator emits, so the trainer UI is unchanged.
//
// A deal with k countable suits serves every level <= k: level 1 asks the first suit,
// level 2 the first two, level 3 all three plus each defender's whole shape. Rounds stop at
// the last question's trick; the reveal then shows the full deal and the result at the table.
// Rules from Paul: no player names (the JSON never has them); BEN's auction is shown only
// when it ends in the contract actually played (the build script enforces that); the
// other room is never mentioned; the screen stays minimal.
import { T } from "./countingHandsGenerator";

const SEAT_WORD = { N: "north", E: "east", S: "south", W: "west" };
const SEAT_NAME = { N: "North", E: "East", S: "South", W: "West" };
const SUIT_SYMBOL = { S: "♠", H: "♥", D: "♦", C: "♣" };
const PARTNER = { N: "S", S: "N", E: "W", W: "E" };
const NEXT = { S: "W", W: "N", N: "E", E: "S" };

let cached = null;
let inflight = null;

/** Fetch the deal file once per page load. Resolves to [] on any failure so the caller can fall back. */
export function loadRealCountDeals() {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  // Day-stamped so a rebuilt file is picked up by the next day at the latest, without a hard-cached copy
  // outliving a deploy (a force-cache fetch kept an old file on 9 Oct 2026).
  const url = `${process.env.PUBLIC_URL || ""}/data/real-count-hands.json?v=${new Date().toISOString().slice(0, 10)}`;
  inflight = fetch(url)
    .then((r) => (r.ok ? r.json() : { deals: [] }))
    .then((d) => { cached = Array.isArray(d.deals) ? d.deals : []; return cached; })
    .catch(() => { cached = []; return cached; });
  return inflight;
}

function contractText(contract) {
  // "6N" -> "6NT", "3CX" -> "3♣ doubled", "4HXX" -> "4♥ redoubled"
  const m = String(contract).match(/^([1-7])([SHDCN])(X{0,2})$/);
  if (!m) return String(contract);
  const strain = m[2] === "N" ? "NT" : SUIT_SYMBOL[m[2]];
  return `${m[1]}${strain}${m[3] === "X" ? " doubled" : m[3] === "XX" ? " redoubled" : ""}`;
}

function vulText(vul) {
  return { None: "Nobody vulnerable", NS: "North-South vulnerable", EW: "East-West vulnerable", All: "Everyone vulnerable" }[vul] || "";
}

const CLOCKWISE = ["N", "E", "S", "W"];

// Rotate the whole deal so the declarer sits South (Paul, 9 Oct 2026: "always rotate it to keep you
// south, just rotate the other hands and the bidding"). Hands, every play, the dealer and the
// vulnerability all turn together, so the record stays consistent; only the compass letters change.
function rotateDeal(deal) {
  const offset = (CLOCKWISE.indexOf("S") - CLOCKWISE.indexOf(deal.declarer) + 4) % 4;
  if (offset === 0) return deal;
  const rot = (seat) => CLOCKWISE[(CLOCKWISE.indexOf(seat) + offset) % 4];
  const hands = {};
  for (const seat of CLOCKWISE) hands[rot(seat)] = deal.hands[seat];
  const vul = offset === 2 ? deal.vul : deal.vul === "NS" ? "EW" : deal.vul === "EW" ? "NS" : deal.vul;
  return {
    ...deal,
    hands,
    dealer: rot(deal.dealer),
    declarer: "S",
    vul,
    rounds: deal.rounds.map((r) => ({
      ...r,
      showOutSeats: (r.showOutSeats || []).map(rot),
      plays: r.plays.map((p) => ({ ...p, seat: rot(p.seat) })),
    })),
    asks: deal.asks.map((a) => ({ ...a, seat: a.seat ? rot(a.seat) : a.seat })),
  };
}

export function dealToPuzzle(rawDeal, mode) {
  const deal = rotateDeal(rawDeal);
  const asks = deal.asks.slice(0, mode);
  const lastAsk = asks[asks.length - 1];
  const decl = deal.declarer;
  const dummy = PARTNER[decl];
  const defenders = [NEXT[decl], NEXT[NEXT[NEXT[decl]]]];
  const rounds = deal.rounds.slice(0, lastAsk.roundIdx + 1).map((r, i) => ({
    label: r.showOutSeats && r.showOutSeats.length
      ? T.trickLabelShowOut(i + 1, r.showOutSeats.map((s) => SEAT_NAME[s]).join(" and "))
      : T.trickLabel(i + 1),
    plays: r.plays.map((p) => ({ seat: p.seat, card: { rank: p.card.rank, suit: p.card.suit }, ...(p.showOut ? { showOut: true } : {}) })),
  }));
  const len = (seat, suit) => (deal.hands[seat][suit] || "").length;
  const customPrompts = asks.map((ask, i) => ({
    id: `rch-${deal.key}-m${mode}-dist${i + 1}`,
    type: "DISTRIBUTION_GUESS",
    suit: ask.suit,
    atRoundIdx: ask.roundIdx,
    promptText: ask.kind === "showOut" ? T.distributionPrompt(ask.suit) : T.distributionPromptExhausted(ask.suit),
    expectedDistribution: {
      north: len("N", ask.suit),
      east: len("E", ask.suit),
      south: len("S", ask.suit),
      west: len("W", ask.suit),
    },
    // Declarer and dummy are face up - their counts come pre-filled, as in the generated deals.
    fixed: { [SEAT_WORD[decl]]: len(decl, ask.suit), [SEAT_WORD[dummy]]: len(dummy, ask.suit) },
    successText: T.distributionSuccess,
  }));
  if (mode === 3 && lastAsk) {
    defenders.forEach((seat, i) => {
      customPrompts.push({
        id: `rch-${deal.key}-m${mode}-shape${i + 1}`,
        type: "SEAT_SHAPE",
        seat: SEAT_WORD[seat],
        atRoundIdx: lastAsk.roundIdx,
        promptText: T.seatShapePrompt(SEAT_NAME[seat]),
        successText: T.distributionSuccess,
      });
    });
  }
  const contract = contractText(deal.contract);
  return {
    id: `rch-${deal.key}-m${mode}`,
    seed: null,
    mode,
    difficulty: mode,
    realDeal: true,
    seatMode: "compass",
    playEngine: "compassClockwise",
    // Compass seat names everywhere (West / North (dummy) / East / You (South)), never LHO/RHO.
    trainerEngine: "compass",
    title: T.titleReal(deal.event, deal.board),
    trumpSuit: deal.trump || null,
    contract,
    dealerCompass: deal.dealer,
    declarerCompass: decl,
    viewerCompass: decl,
    vulnerability: vulText(deal.vul),
    visibleFullHandSeats: [SEAT_WORD[decl], SEAT_WORD[dummy]],
    revealFullHandsAtEnd: defenders.map((s) => SEAT_WORD[s]),
    ...(deal.auction ? { auction: deal.auction.join(" ") } : {}),
    promptOptions: {
      promptPlacement: "left",
      hideAuction: !deal.auction,
      keepRailContextOpen: true,
      contractLabel: T.contractLabelReal(contract, SEAT_NAME[decl], deal.tricksMade, deal.claimed, deal.event, deal.board),
      disableWarmupTrumpGuess: true,
      questionNumbers: [],
      customPrompts,
    },
    shownHands: {
      north: { ...deal.hands.N },
      east: { ...deal.hands.E },
      south: { ...deal.hands.S },
      west: { ...deal.hands.W },
    },
    rounds,
    ...(deal.note ? { authorNote: deal.note } : {}),
  };
}

function shuffle(arr, rng) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
}

/** A batch of puzzles for a level: every deal with at least `mode` countable suits, shuffled. */
export function buildRealPuzzles(deals, mode, count = 16, rng = Math.random) {
  const eligible = (deals || []).filter((d) => Array.isArray(d.asks) && d.asks.length >= mode);
  return shuffle(eligible, rng).slice(0, count).map((d) => dealToPuzzle(d, mode));
}
